import React, { useState, useEffect } from 'react';
import { Search, Plus, Package, Loader2, Check, X, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerListings = () => {
  const { token } = useAuth();
  const [myListings, setMyListings] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newListing, setNewListing] = useState({ price: '', stock: '', cargo_time: '3' });
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchMyListings();
  }, []);

  const fetchMyListings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seller/listings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMyListings(data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchCatalog = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      toast.error('En az 2 karakter girin');
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/seller/catalog/search?q=${encodeURIComponent(searchTerm)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCatalogProducts(data.products || []);
      if (data.products?.length === 0) {
        toast.info('Ürün bulunamadı');
      }
    } catch (error) {
      toast.error('Arama başarısız');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddListing = async () => {
    if (!selectedProduct || !newListing.price || !newListing.stock) {
      toast.error('Fiyat ve stok giriniz');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/seller/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          price: parseFloat(newListing.price),
          stock: parseInt(newListing.stock),
          cargo_time: parseInt(newListing.cargo_time)
        })
      });
      
      if (res.ok) {
        toast.success('Ürün listelendi!');
        setShowAddDialog(false);
        setSelectedProduct(null);
        setCatalogProducts([]);
        setSearchTerm('');
        setNewListing({ price: '', stock: '', cargo_time: '3' });
        fetchMyListings();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Listeleme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (listing) => {
    setEditingId(listing.id);
    setEditData({
      price: listing.price,
      stock: listing.stock,
      cargo_time: listing.cargo_time || 3
    });
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seller/listings/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          price: parseFloat(editData.price),
          stock: parseInt(editData.stock),
          cargo_time: parseInt(editData.cargo_time)
        })
      });
      
      if (res.ok) {
        toast.success('Güncellendi');
        setEditingId(null);
        fetchMyListings();
      }
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Bu ürünü listeden kaldırmak istediğinize emin misiniz?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/seller/listings/${listingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Ürün kaldırıldı');
        fetchMyListings();
      }
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Ürün Listele</h1>
          <p className="text-gray-500">Pazarlo kataloğundan ürün seçip fiyat ve stok belirleyin</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Ürün Ekle
        </Button>
      </div>

      {/* Mevcut Listelerim */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Listelediğim Ürünler ({myListings.length})</h2>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" />
          </div>
        ) : myListings.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Henüz ürün listelemediniz</p>
            <Button 
              onClick={() => setShowAddDialog(true)} 
              className="mt-4 bg-orange-500 hover:bg-orange-600"
            >
              İlk Ürünü Ekle
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fiyat</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stok</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kargo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Öne Çıkan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {myListings.map((listing) => (
                  <tr key={listing.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {listing.image_url ? (
                          <img src={listing.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm line-clamp-1">{listing.product_name}</p>
                          <p className="text-xs text-gray-500">{listing.oem_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingId === listing.id ? (
                        <Input
                          type="number"
                          value={editData.price}
                          onChange={(e) => setEditData({...editData, price: e.target.value})}
                          className="w-24"
                        />
                      ) : (
                        <span className="font-bold text-orange-600">{listing.price?.toLocaleString()} ₺</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === listing.id ? (
                        <Input
                          type="number"
                          value={editData.stock}
                          onChange={(e) => setEditData({...editData, stock: e.target.value})}
                          className="w-20"
                        />
                      ) : (
                        <span className={listing.stock > 10 ? 'text-green-600' : listing.stock > 0 ? 'text-orange-600' : 'text-red-600'}>
                          {listing.stock} adet
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === listing.id ? (
                        <select
                          value={editData.cargo_time}
                          onChange={(e) => setEditData({...editData, cargo_time: e.target.value})}
                          className="border rounded px-2 py-1"
                        >
                          <option value="1">1 gün</option>
                          <option value="2">2 gün</option>
                          <option value="3">3 gün</option>
                          <option value="5">5 gün</option>
                        </select>
                      ) : (
                        <span>{listing.cargo_time || 3} gün</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-orange-500"
                            style={{ width: `${Math.min(100, listing.buy_box_score || 0)}%` }}
                          />
                        </div>
                        <span className="text-sm">{(listing.buy_box_score || 0).toFixed(0)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingId === listing.id ? (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEdit} className="bg-green-500 hover:bg-green-600">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => startEdit(listing)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteListing(listing.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ürün Ekleme Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Katalogdan Ürün Ekle</h2>
                <button onClick={() => { setShowAddDialog(false); setSelectedProduct(null); setCatalogProducts([]); }}>
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {!selectedProduct ? (
                <>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="SKU, ürün adı veya OEM no ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchCatalog()}
                      className="flex-1"
                    />
                    <Button onClick={searchCatalog} disabled={searchLoading} className="bg-orange-500 hover:bg-orange-600">
                      {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>

                  {catalogProducts.length > 0 && (
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      {catalogProducts.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => setSelectedProduct(product)}
                          className="w-full p-3 flex items-center gap-3 hover:bg-orange-50 border-b last:border-b-0 text-left"
                        >
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.oem_code} | {product.brand}</p>
                          </div>
                          <span className="text-orange-600 font-medium whitespace-nowrap">
                            {product.base_price?.toLocaleString()} ₺
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-4">
                      {selectedProduct.image_url ? (
                        <img src={selectedProduct.image_url} alt="" className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-bold">{selectedProduct.name}</p>
                        <p className="text-sm text-gray-500">{selectedProduct.oem_code} | {selectedProduct.brand}</p>
                        <p className="text-sm text-gray-500">Taban Fiyat: {selectedProduct.base_price?.toLocaleString()} ₺</p>
                      </div>
                      <button onClick={() => setSelectedProduct(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Satış Fiyatı (₺) *</label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={newListing.price}
                        onChange={(e) => setNewListing({...newListing, price: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stok Adedi *</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={newListing.stock}
                        onChange={(e) => setNewListing({...newListing, stock: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Kargo Süresi</label>
                      <select
                        value={newListing.cargo_time}
                        onChange={(e) => setNewListing({...newListing, cargo_time: e.target.value})}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="1">1 Gün</option>
                        <option value="2">2 Gün</option>
                        <option value="3">3 Gün</option>
                        <option value="5">5 Gün</option>
                        <option value="7">7 Gün</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowAddDialog(false); setSelectedProduct(null); setCatalogProducts([]); }}>
                İptal
              </Button>
              <Button 
                onClick={handleAddListing} 
                disabled={!selectedProduct || !newListing.price || !newListing.stock || submitting}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Ürünü Listele
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerListings;
